import { why } from '~/api/why'
import { waitingChanged } from '~/social/waiting'

/**
 * The Names page's answers on their way to the server: one at a time, in the order pressed.
 *
 * The page moves on at the press and the write follows behind it, so what the editor sees is
 * the press and what the server sees is the same sequence of requests it saw when every press
 * waited for the last. One at a time because each write is decided under the server's one lock
 * against what the writes before it left behind — a fold, then an undo of it, sent together,
 * could be answered in the wrong order — and because the Tidy card and the spellings list are
 * one queue, a row that is both a suspect and a collision is never written from two sides at
 * once.
 *
 * Held outside React, and owned by the page that made it, so that leaving the page does not
 * drop what was pressed: the chain of answers keeps the queue alive until it drains, and with
 * nobody subscribed there is nobody to show a refusal to.
 */

/** A verdict on a suspect, an undo of one, or an answer to a spelling. */
export type Kind = 'verdict' | 'undo' | 'spelling'

/**
 * `queued` is not yet sent; `out` is the one in flight. `cancelled` was never sent: an undo of a
 * verdict still queued, or an undo whose verdict was refused, so there is nothing to undo.
 */
export type State = 'queued' | 'out' | 'landed' | 'refused' | 'cancelled'

/** The part of a `/tidy` or `/spelling` answer the page reads. */
export interface Answer {
  waiting: number
  undone?: number
}

export interface Write {
  /** Press order, from the same clock as `settled`, so "pressed after that settled" is a comparison. */
  readonly seq: number
  readonly kind: Kind
  /** The row the press was about — the row decided, not where its answer says the bills went. */
  readonly row: number
  /** For an undo, the `seq` of the verdict it takes back. */
  readonly takes?: number
  readonly state: State
  readonly answer?: Answer
  /** The sentence for a refusal, from `why`. */
  readonly said?: string
  readonly settled?: number
}

export interface Seen {
  /** The server's count, from the last write that landed, or from `/suspects` before any has. */
  readonly base: number
  readonly writes: readonly Write[]
}

/** Not yet answered: what the page has already moved for and the server has not yet seen. */
export function pending(write: Write): boolean {
  return write.state === 'queued' || write.state === 'out'
}

/**
 * The count the page shows: the server's last figure, less what is still on its way.
 *
 * The server's figure is the base rather than a running tally, because one verdict can move the
 * count by more than one — a fold takes its members with it, a split can add suspects — and only
 * the server knows by how much. Each answer corrects whatever the local arithmetic got wrong.
 */
export function count(seen: Seen): number {
  let n = seen.base
  for (const write of seen.writes) {
    if (!pending(write)) continue
    n += write.kind === 'undo' ? 1 : -1
  }
  return n
}

/** The newest write of `kind` about `row`, if there is one. */
export function latest(seen: Seen, kind: Kind, row: number): Write | undefined {
  for (let at = seen.writes.length - 1; at >= 0; at -= 1) {
    const write = seen.writes[at]
    if (write && write.kind === kind && write.row === row) return write
  }
  return undefined
}

/**
 * A button's handlers that act once per press: they ignore the second click of a double click,
 * and the clicks a held Enter repeats.
 *
 * The page moves on at the press, so the row under the pointer — or under the focus, since the
 * card's buttons are the same elements from row to row — changes between one click and the
 * next, and a second click would answer the next row with the same verdict.
 * Undo is the other way about: it stays put while the verdicts behind it change, so a second
 * click would take back the verdict before the one the first click took.
 *
 * `detail` counts the clicks in a run of the pointer, so 2 or more is a double click. No timer of
 * the page's own: what counts as one run is the browser's double-click rule, so a second press
 * that falls outside it is a first click again.
 *
 * A click the keyboard made has `detail` 0 every time, held key or not, so it cannot be told
 * apart there. Enter activates a button on each keydown, auto-repeats included, so the repeats
 * are stopped where they are marked — `repeat` on the keydown — and cancelling that keydown is
 * what keeps its click from being made. Space activates on keyup, which a held key sends once,
 * so it needs nothing. Only Enter is cancelled: a repeated Tab or arrow still moves the focus.
 */
export function onePress(act: () => void): {
  onClick: (event: { detail: number }) => void
  onKeyDown: (event: { key: string; repeat: boolean; preventDefault: () => void }) => void
} {
  return {
    onClick: event => {
      if (event.detail < 2) act()
    },
    onKeyDown: event => {
      if (event.key === 'Enter' && event.repeat) event.preventDefault()
    },
  }
}

export class Writes {
  private seen: Seen = { base: 0, writes: [] }
  private readonly sends = new Map<number, () => Promise<Answer>>()
  private readonly readers = new Set<() => void>()
  private readonly waiters: (() => void)[] = []
  private clock = 0
  /** Whether anything has landed since the queue last drained, which is what the bar is told. */
  private moved = false

  subscribe = (tell: () => void): (() => void) => {
    this.readers.add(tell)
    return () => {
      this.readers.delete(tell)
    }
  }

  read = (): Seen => this.seen

  /** The count `/suspects` answered with, before any write has landed. */
  start(waiting: number): void {
    this.set({ ...this.seen, base: waiting })
  }

  /**
   * Queue a write, and send it now if nothing is in flight.
   *
   * `send` is the request exactly as the press built it, so what goes is what was on screen
   * when it was pressed, whatever the boxes say by the time it is sent.
   */
  press(kind: Kind, row: number, send: () => Promise<Answer>, takes?: number): number {
    this.clock += 1
    const seq = this.clock
    this.sends.set(seq, send)
    const write: Write = takes === undefined
      ? { seq, kind, row, state: 'queued' }
      : { seq, kind, row, takes, state: 'queued' }
    this.set({ ...this.seen, writes: [...this.seen.writes, write] })
    this.pump()
    return seq
  }

  /** Take back a write that has not been sent. One already out cannot be recalled. */
  cancel(seq: number): void {
    const write = this.find(seq)
    if (write?.state !== 'queued') return
    this.sends.delete(seq)
    this.mark(seq, { state: 'cancelled' })
    this.pump()
  }

  /** Settles once nothing is queued or in flight — at once, if that is already so. */
  drained(): Promise<void> {
    if (!this.seen.writes.some(pending)) return Promise.resolve()
    return new Promise(resolve => this.waiters.push(resolve))
  }

  private find(seq: number): Write | undefined {
    return this.seen.writes.find(write => write.seq === seq)
  }

  private set(seen: Seen): void {
    this.seen = seen
    for (const tell of this.readers) tell()
  }

  private mark(seq: number, change: Partial<Write>, base = this.seen.base): void {
    this.set({
      base,
      writes: this.seen.writes.map(write => (write.seq === seq ? { ...write, ...change } : write)),
    })
  }

  private pump(): void {
    if (this.seen.writes.some(write => write.state === 'out')) return
    for (;;) {
      const head = this.seen.writes.find(write => write.state === 'queued')
      if (!head) {
        this.drain()
        return
      }
      const send = this.sends.get(head.seq)
      this.sends.delete(head.seq)
      // An undo goes only behind a verdict that landed. The verdict was in flight or landed
      // when the undo was pressed, and everything ahead of the undo has settled by the time
      // it is the head, so anything but `landed` here means the server refused the verdict
      // and holds nothing to put back.
      const taken = head.takes === undefined ? undefined : this.find(head.takes)
      if (!send || (taken && taken.state !== 'landed')) {
        this.mark(head.seq, { state: 'cancelled' })
        continue
      }
      this.mark(head.seq, { state: 'out' })
      let sent: Promise<Answer>
      try {
        sent = send()
      } catch (error) {
        sent = Promise.reject(error)
      }
      Promise.resolve(sent).then(
        answer => this.settle(head.seq, { state: 'landed', answer }),
        (error: unknown) => this.settle(head.seq, { state: 'refused', said: why(error) }),
      )
      return
    }
  }

  private settle(seq: number, change: Partial<Write>): void {
    try {
      this.clock += 1
      if (change.state === 'landed') this.moved = true
      // An answer without a count leaves the last one standing rather than showing `NaN`.
      const waiting = change.answer?.waiting
      const base = typeof waiting === 'number' ? waiting : this.seen.base
      this.mark(seq, { ...change, settled: this.clock }, base)
    } finally {
      // Whatever painting the answer did, the next write still goes: a queue that stopped on
      // a throw here would hold every later press, unsent, with the page saying they were made.
      this.pump()
    }
  }

  /**
   * Nothing queued and nothing out.
   *
   * The bar fetches its count once per page load, so without being told the badge holds its
   * opening number while the queue empties under it. It is told here, once, rather than per
   * write: its count is a `/waiting` request, and the server answers that under the same lock
   * as the writes, so one per write would put a request between every pair of them. A drain in
   * which nothing landed changed nothing.
   */
  private drain(): void {
    if (this.moved) {
      this.moved = false
      waitingChanged()
    }
    for (const resolve of this.waiters.splice(0)) resolve()
  }
}
